import { useCallback, useEffect, useMemo, useRef } from 'react'

import { CaptchaAction, createTokenMinter, TokenMinter } from '@percent/utility'
import { CaptchaContext } from './CaptchaContext'
import { CaptchaContextControllerProps } from './CaptchaContext.types'
import { config } from '@percent/education-hosted-form/config/config'

export function CaptchaContextController({ children, scriptLoadTimeoutMs }: CaptchaContextControllerProps) {
  const sitekey = config.keys.googleCaptcha

  // No feature flag on purpose. Ad blockers block PostHog, so the flag would read `false`
  // and drop the token when the API needs it. The site key is the switch.
  const isEnabled = !!sitekey

  const minterRef = useRef<TokenMinter>()

  // Built on first use rather than in an effect: an effect leaves a window after mount where
  // `getToken` answers `undefined` and the request goes out with no header.
  const getMinter = useCallback(() => {
    if (!isEnabled) {
      return undefined
    }

    if (!minterRef.current) {
      const host = document.createElement('div')
      document.body.appendChild(host)

      minterRef.current = createTokenMinter(sitekey, host, scriptLoadTimeoutMs)
    }

    return minterRef.current
  }, [isEnabled, sitekey, scriptLoadTimeoutMs])

  const reset = useCallback(() => {
    minterRef.current?.reset()
  }, [])

  const getToken = useCallback(
    async (action: CaptchaAction) => {
      try {
        // A throw must not fail the submission. Only the API decides whether a missing token is
        // fatal, and `grecaptcha.render` throws outright on a misconfigured key.
        const token = await getMinter()?.getToken(action)

        return token ?? undefined
      } catch {
        return undefined
      } finally {
        // Tokens are single use. Drop the spent one now, or the next challenge returns it.
        minterRef.current?.reset()
      }
    },
    [getMinter]
  )

  useEffect(
    () => () => {
      minterRef.current?.destroy()
      minterRef.current = undefined
    },
    []
  )

  const contextValue = useMemo(() => ({ isEnabled, getToken, reset }), [isEnabled, getToken, reset])

  return <CaptchaContext.Provider value={contextValue}>{children}</CaptchaContext.Provider>
}
